import {
  ChangeDetectionStrategy,
  AfterViewInit,
  Component,
  ElementRef,
  NgZone,
  OnInit,
  ViewChild,
  effect,
  signal,
  input,
  output,
} from "@angular/core";
import { ILocation } from "src/interfaces/interfaces";
import { BrandService } from "src/services/brand.service";
import { StockholmCenterCoordinates } from "src/domain/constants";
import { ModelAndDealerService } from "src/services/model-and-dealer.service";
import { GoogleMapsLatLngLitPipe } from "src/pipes/google-maps-lat-lng-lit.pipe";
import { GoogleMap, MapMarker } from "@angular/google-maps";
import { TranslateModule } from "@ngx-translate/core";
import { GoogleMapsLatLngLitPipe as GoogleMapsLatLngLitPipe_1 } from "../../../pipes/google-maps-lat-lng-lit.pipe";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "vw-google-map",
  templateUrl: "./google-map.component.html",
  imports: [GoogleMap, MapMarker, TranslateModule, GoogleMapsLatLngLitPipe_1],
})
export class GoogleMapComponent implements OnInit, AfterViewInit {
  mapLocation: google.maps.LatLngLiteral = {
    lat: StockholmCenterCoordinates.lat,
    lng: StockholmCenterCoordinates.lng,
  };
  zoom = 10;
  mapEnabled = true;
  readonly geoLocationEnabled = signal(false);

  readonly allLocations = input<ILocation[]>();
  readonly clientLocation = input<ILocation>();
  readonly selectedLocation = input<ILocation>();

  readonly locationSelected = output<ILocation>();
  readonly locationSearched = output<ILocation>();
  readonly markerClick = output<ILocation>();

  @ViewChild("search") public searchElementRef?: ElementRef;

  options: google.maps.MapOptions = {};
  placeIcon: google.maps.MarkerOptions = {};
  selectedPlaceIcon: google.maps.MarkerOptions = {};
  clientIcon: google.maps.MarkerOptions = {};
  stockholmCenterCoordinates: google.maps.LatLngLiteral = {
    lat: StockholmCenterCoordinates.lat,
    lng: StockholmCenterCoordinates.lng,
  };

  constructor(
    private ngZone: NgZone,
    private brandService: BrandService,
    public modelAndDealerService: ModelAndDealerService,
    private latLngLitPipe: GoogleMapsLatLngLitPipe,
  ) {
    effect(() => {
      const currentValue = this.selectedLocation();
      if (currentValue != null) {
        this.mapLocation = {
          lat: currentValue.latitude,
          lng: currentValue.longitude,
        };
      }
    });
  }

  ngOnInit(): void {
    this.initMap();
  }

  ngAfterViewInit(): void {
    this.initAutocomplete();
  }

  initMap(): void {
    navigator.permissions.query({ name: "geolocation" }).then((e) => {
      if (e.state === "granted") {
        this.geoLocationEnabled.set(true);
      } else {
        this.geoLocationEnabled.set(false);
      }
    });

    const selectedLocation = this.selectedLocation();
    if (!!selectedLocation) {
      this.mapLocation = this.latLngLitPipe.transform(
        selectedLocation,
        this.stockholmCenterCoordinates,
      );
    } else if (!!this.clientLocation()) {
      this.mapLocation = this.latLngLitPipe.transform(
        this.clientLocation(),
        this.stockholmCenterCoordinates,
      );
    } else {
      this.mapLocation = this.stockholmCenterCoordinates;
    }
    this.options = {
      zoomControl: true,
      mapTypeControl: false,
      scaleControl: false,
      streetViewControl: false,
      rotateControl: false,
      fullscreenControl: false,
    };
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode(
      {
        location: this.mapLocation,
      },
      (results, status: string) => {
        if (status === "OK" && this.searchElementRef) {
          this.searchElementRef.nativeElement.value =
            results?.[0].formatted_address;
        }
      },
    );
    this.initIcons();
  }

  initIcons(): void {
    this.placeIcon = {
      icon: {
        url: "./assets/img/map/no-place-24px.svg",
        scaledSize: new google.maps.Size(33, 33),
      },
      zIndex: 101,
      clickable: true,
    };
    this.selectedPlaceIcon = {
      icon: {
        url: `./assets/img/map/${this.brandService.brand?.urlShort}/place-24px.svg`,
        scaledSize: new google.maps.Size(33, 33),
      },
      zIndex: 101,
      clickable: true,
    };
    this.clientIcon = {
      icon: {
        url: `./assets/img/map/${this.brandService.brand?.urlShort}/client_location.svg`,
        scaledSize: new google.maps.Size(33, 33),
      },
      zIndex: 100,
      clickable: false,
    };
  }

  initAutocomplete(): void {
    const options = {
      componentRestrictions: { country: "se" },
    };
    const autocomplete = new google.maps.places.Autocomplete(
      this.searchElementRef?.nativeElement,
      options,
    );
    autocomplete.addListener("place_changed", () => {
      this.ngZone.run(() => {
        // get the place result
        const place: google.maps.places.PlaceResult = autocomplete.getPlace();

        // verify result
        if (place.geometry === undefined || place.geometry === null) {
          return;
        }
        // set latitude, longitude and zoom
        this.mapLocation = this.latLngLitPipe.transform(
          place.geometry.location,
          this.stockholmCenterCoordinates,
        );
        // reposition map
        this.locationSearched.emit({
          longitude: this.mapLocation.lng,
          latitude: this.mapLocation.lat,
        });
      });
    });
  }

  toggleMap(): void {
    this.mapEnabled = !this.mapEnabled;
  }

  onMarkerClick(event: google.maps.MapMouseEvent): void {
    this.mapLocation = this.latLngLitPipe.transform(
      event.latLng,
      this.stockholmCenterCoordinates,
    );
    this.markerClick.emit({
      longitude: this.mapLocation.lng,
      latitude: this.mapLocation.lat,
    });
  }

  moveMapLocation(event: google.maps.MapMouseEvent): void {
    if (event.latLng != null) {
      this.mapLocation = this.latLngLitPipe.transform(
        event.latLng,
        this.stockholmCenterCoordinates,
      );
    }
  }

  useClientLocation(): void {
    this.mapLocation = this.latLngLitPipe.transform(
      this.clientLocation(),
      this.stockholmCenterCoordinates,
    );
  }

  onSearch(value: string): void {
    const service = new google.maps.places.AutocompleteService();
    service.getPlacePredictions({ input: value }, (predictions, status) => {
      if (status !== google.maps.places.PlacesServiceStatus.OK) {
        return;
      }
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode(
        {
          placeId: predictions?.[0].place_id,
        },
        (responses) => {
          this.ngZone.run(() => {
            const firstResponse = responses?.[0];
            // set latitude, longitude and zoom
            if (this.searchElementRef)
              this.searchElementRef.nativeElement.value =
                firstResponse?.formatted_address;
            this.mapLocation = this.latLngLitPipe.transform(
              firstResponse?.geometry?.location,
              this.stockholmCenterCoordinates,
            );
            this.locationSearched.emit({
              longitude: this.mapLocation.lng,
              latitude: this.mapLocation.lat,
            });
          });
        },
      );
    });
  }
}
